import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  FormControlLabel,
  Grid,
  Modal,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useMediaQuery } from "react-responsive";
import { useTheme } from "@mui/material/styles";

import {
  Card,
  MonthlyComparativeCard,
  ComparativeCardSkeleton,
  TableCardSkeleton,
} from "../../components/Cards";
import LayoutWrapper from "../../components/LayoutWrapper/LayoutWrapper";
import NativeObject from "../../components/NativeObject/NativeObject";
import { useInsightsData } from "../../hooks";
import { CircularProgress, Box } from "@mui/material";
import { useSelections } from "../../context/Selections";
import { ReactComponent as IconLogoFooter } from "../../assets/icons/footer/logo-footer.svg";

const NATIVE_OBJECTS = {
  GRAF_01: "WkWjQ",
  TABLE_01: "VjgeaW",
  GRAF_02: "gWTrAdq",
  TABLE_02: "VCLJPDT",
  POTENTIAL_DISTRIBUTORS: "jXPSjZ",
};

const ComparativeSection = ({
  data,
  percentages,
  isLoading,
  gridStyles,
  app,
}) => {
  const [updateDate, setUpdateDate] = useState();
  const { selections } = useSelections();

  const getUpdateDate = useCallback(
    async (objectId) => {
      if (app && objectId) {
        let id = objectId;
        await app.visualization.get(id).then((res) => {
          const value = res?.model?.layout?.style?.label;
          setUpdateDate(value);
        });
      }
    },
    [app]
  );

  useEffect(() => {
    if (app) {
      getUpdateDate("jkfJxS");
    }
  }, [app, getUpdateDate, selections]);
  if (isLoading) {
    return (
      <Grid
        container
        className="container-cards-bignumbers"
        sx={{
          ...gridStyles.container,
          justifyContent: "space-between",
          alignItems: "stretch",
          mb: 2,
        }}
      >
        <Grid item xs={12} sm={5.95} sx={gridStyles.common}>
          <ComparativeCardSkeleton />
        </Grid>
        <Grid item xs={12} sm={5.95} sx={gridStyles.common}>
          <ComparativeCardSkeleton />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      className="container-cards-bignumbers"
      sx={{
        ...gridStyles.container,
        justifyContent: "space-between",
        alignItems: "stretch",
        mb: 2,
      }}
    >
      <Grid item xs={12} sm={5.95} sx={gridStyles.common}>
        <MonthlyComparativeCard data={data} percentage={percentages.monthly} />
      </Grid>
      <Grid item xs={12} sm={5.95} sx={gridStyles.common}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          backgroundColor={"rgb(255, 255, 255)"}
          borderRadius={"8px"}
          boxShadow={"rgba(0, 0, 0, 0.05) 0px 2px 4px"}
          alignItems={"center"}
          padding={"20px"}
          height={"100%"}
        >
          <Box fontSize={"24px"} fontWeight={"bold"}>
            <span>Atualizado em</span> <span>{updateDate}</span>
          </Box>

          <div>
            <IconLogoFooter width={"76px"} height={"30px"} />
          </div>
        </Box>
      </Grid>
    </Grid>
  );
};

const TablesSection = ({ app, onOpenModal, isLoading, gridStyles }) => (
  <>
    <Grid
      container
      spacing={2}
      sx={{
        ...gridStyles.container,
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <Grid item xs={12} sm={5.95} sx={{ ...gridStyles.common, mr: 1 }}>
        {isLoading ? (
          <TableCardSkeleton />
        ) : (
          <PotentialDistributorsTable
            app={app}
            onDetailClick={onOpenModal}
            isLoading={isLoading}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={5.95} sx={gridStyles.common}>
        {isLoading ? (
          <TableCardSkeleton />
        ) : (
          <PotentialCategoriesTable app={app} />
        )}
      </Grid>
    </Grid>

    <Grid
      container
      sx={{
        ...gridStyles.container,
        justifyContent: "center",
        alignItems: "flex-start",
        mt: 2,
      }}
    >
      <Grid item xs={12} sx={gridStyles.common}>
        {isLoading ? (
          <TableCardSkeleton />
        ) : (
          <DistributorsWithPotentialTable app={app} />
        )}
      </Grid>
    </Grid>
  </>
);

const TableSkeleton = () => (
  <Box
    sx={{
      width: "100%",
      height: "365px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
    }}
  >
    <CircularProgress />
  </Box>
);

const PotentialDistributorsTable = ({ app, onDetailClick, isLoading }) => (
  <Card
    app={app}
    header={{ hardTitle: "Histórico de Performance" }}
    className="card-distribuidores-potencial"
    exportTable
    exportTableId={NATIVE_OBJECTS.TABLE_01}
  >
    {isLoading ? (
      <TableSkeleton />
    ) : (
      <div style={{ width: "100%", height: "650px" }}>
        <NativeObject
          app={app}
          key={NATIVE_OBJECTS.GRAF_01}
          reference={{ id: NATIVE_OBJECTS.GRAF_01 }}
          style={{ width: "100%", height: "300px" }}
        />

        <NativeObject
          app={app}
          key={NATIVE_OBJECTS.TABLE_01}
          reference={{ id: NATIVE_OBJECTS.TABLE_01 }}
          style={{ width: "100%", height: "300px", marginTop: "50px" }}
        />
      </div>
    )}
  </Card>
);

const PotentialCategoriesTable = ({ app }) => (
  <Card
    app={app}
    header={{ hardTitle: "Performance Categoria X Meta" }}
    className="card-menor"
    exportContainer
    exportTable
    exportTableId={NATIVE_OBJECTS.TABLE_02}
    tooltipText="No gráfico apuramos a média do período total."
  >
    <div style={{ width: "100%", height: "650px" }}>
      <NativeObject
        app={app}
        key={NATIVE_OBJECTS.GRAF_02}
        reference={{ id: NATIVE_OBJECTS.GRAF_02 }}
        style={{ width: "100%", height: "300px" }}
      />

      <NativeObject
        app={app}
        key={NATIVE_OBJECTS.TABLE_02}
        reference={{ id: NATIVE_OBJECTS.TABLE_02 }}
        style={{ width: "100%", height: "300px", marginTop: "50px" }}
      />
    </div>
  </Card>
);

const DistributorsWithPotentialTable = ({ app }) => {
  const { selections } = useSelections();
  const [selectedValueVision, setSelectedValueVision] = useState("Resumo");
  useEffect(() => {
    if (app) {
      const vision = selections.length
        ? selections.find((s) => s.qField === "Tipo_Visao")
        : null;
      setSelectedValueVision(vision?.qSelected);
    }
  }, [app, selections]);

  const handleChange = (event) => {
    app.field("Tipo_Visao").selectMatch(event.target.value, true);
    setSelectedValueVision(event.target.value);
  };
  return (
    <Card
      app={app}
      header={{ hardTitle: "Positivações em Potencial (Churn)" }}
      className="card-distribuidores-potencial"
      exportContainer
      exportTable
      exportTableId={NATIVE_OBJECTS.POTENTIAL_DISTRIBUTORS}
      tooltipText={
        selectedValueVision === "Detalhado"
          ? `
          Clientes com compra = 1;
          Clientes sem compra = 0;
          Novo = cliente sem compra no mês anterior e com compra no mês vigente;
          Recorrente = Cliente com compra no mês anterior e com compra no mês vigente;
          Sem Compras = Cliente que não realizou compras no mês vigente.
        `
          : `
            Novo = cliente sem compra no mês anterior e com compra no mês vigente;
            Recorrente = Cliente com compra no mês anterior e com compra no mês vigente;
            Sem Compras = Cliente que não realizou compras no mês vigente.
          `
      }
    >
      <FormControl sx={{ marginTop: "-4px", marginBottom: "19px" }}>
        <RadioGroup
          defaultValue="Resumo"
          row
          name="row-radio-buttons-group"
          className="radio-group radio-group-button-export"
          value={selectedValueVision}
          onChange={handleChange}
        >
          <FormControlLabel
            value="Resumo"
            control={
              <Radio
                size="small"
                defaultChecked
                sx={{
                  color: "#00833c",
                  "&.Mui-checked": {
                    color: "#00833c",
                  },
                }}
              />
            }
            label="Resumo"
          />

          <FormControlLabel
            value="Detalhado"
            control={
              <Radio
                size="small"
                sx={{
                  color: "#00833c",
                  "&.Mui-checked": {
                    color: "#00833c",
                  },
                }}
              />
            }
            label="Detalhado"
          />
        </RadioGroup>
      </FormControl>

      <NativeObject
        app={app}
        key={NATIVE_OBJECTS.POTENTIAL_DISTRIBUTORS}
        reference={{ id: NATIVE_OBJECTS.POTENTIAL_DISTRIBUTORS }}
        style={{ width: "100%", height: "365px" }}
      />
    </Card>
  );
};

const DetailModal = ({ app, open, onClose, notFullHd }) => (
  <Modal open={open} onClose={onClose}>
    <Grid
      className="card"
      flexDirection="column"
      justifyContent="center"
      sx={{
        top: notFullHd ? "52%" : "44%",
        left: notFullHd ? "50%" : "0",
        transform: "translate(-50%, -50%)",
        position: "absolute",
        height: "auto",
        width: notFullHd ? "70%" : "20%",
      }}
    >
      <Grid
        className="modal-content"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        sx={{
          height: "100%",
        }}
      >
        <NativeObject
          app={app}
          key={NATIVE_OBJECTS.DISTRIBUTORS}
          reference={{ id: NATIVE_OBJECTS.DISTRIBUTORS }}
          style={{
            width: "100%",
            height: "400px",
          }}
        />
      </Grid>
    </Grid>
  </Modal>
);

function InsightsDesktop({ app }) {
  const theme = useTheme();
  const notFullHd = useMediaQuery({ query: "(max-width: 1536px)" });
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { data, percentages } = useInsightsData(app);

  const GRID_STYLES = {
    common: {
      display: "flex",
      flex: 1,
      "& > *": {
        width: "100%",
      },
      p: 0,
    },
    container: {
      mb: 0,
      width: "100%",
      maxWidth: "100%",
      mx: "auto",
      "& .MuiGrid-item": {
        pl: "0 !important",
        pr: "0 !important",
        [theme.breakpoints.down("sm")]: {
          maxWidth: "100% !important",
          flex: "0 0 100% !important",
        },
      },
    },
  };

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  }, []);

  return (
    <div className="app insights">
      <div className="appContent">
        <LayoutWrapper>
          <ComparativeSection
            data={data}
            percentages={percentages}
            isLoading={isLoading}
            gridStyles={GRID_STYLES}
            app={app}
          />
          <TablesSection
            app={app}
            onOpenModal={() => setOpenModal(true)}
            isLoading={isLoading}
            gridStyles={GRID_STYLES}
          />
          <DetailModal
            app={app}
            open={openModal}
            onClose={() => setOpenModal(false)}
            notFullHd={notFullHd}
          />
        </LayoutWrapper>
      </div>
    </div>
  );
}

// PropTypes
const componentPropTypes = {
  app: PropTypes.object,
  data: PropTypes.object,
  percentages: PropTypes.object,
  onDetailClick: PropTypes.func,
  onOpenModal: PropTypes.func,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  notFullHd: PropTypes.bool,
};

InsightsDesktop.propTypes = componentPropTypes;
ComparativeSection.propTypes = componentPropTypes;
TablesSection.propTypes = componentPropTypes;
PotentialDistributorsTable.propTypes = componentPropTypes;
PotentialCategoriesTable.propTypes = componentPropTypes;
DistributorsWithPotentialTable.propTypes = componentPropTypes;
DetailModal.propTypes = componentPropTypes;

InsightsDesktop.defaultProps = {
  app: null,
};

export default InsightsDesktop;
